import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  List,
  ListItem,
  TextField,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { WrapperSeamless } from "../../layout-components";
import { Select } from "../../components";
import * as Api from "../../service";
import useStore from "../../context";
import { useTranslation } from "localization/translation";

const LANG_OPTIONS = [
  { id: "en", name: "English" },
  { id: "ru", name: "Russian" },
  { id: "hy", name: "Armenian" },
];

const GROUP_ORDER = [
  "registration",
  "auth",
  "account",
  "trial",
  "subscription",
  "post_deactivate",
  "payment",
  "other",
];

const AUDIENCE_TABS = ["direct", "partner"];

const matchesAudience = (item, audience) => {
  const itemAudience = item.audience || "shared";
  return itemAudience === audience || itemAudience === "shared";
};

export default function EmailTestPage() {
  const translation = useTranslation();
  const { setErrorMsg, setSuccessMsg } = useStore();
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [userId, setUserId] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [lang, setLang] = useState("en");
  const [audience, setAudience] = useState("direct");
  const [sendingType, setSendingType] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const loadTypes = useCallback(async () => {
    setLoadingTypes(true);
    try {
      const res = await Api.emailTest.getTypes();
      setTypes(res?.data || []);
    } catch (err) {
      setTypes([]);
      setErrorMsg(err?.message || translation.something_went_wrong);
    } finally {
      setLoadingTypes(false);
    }
  }, [setErrorMsg, translation.something_went_wrong]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const filteredTypes = useMemo(
    () => types.filter((item) => matchesAudience(item, audience)),
    [types, audience]
  );

  const groupedTypes = useMemo(() => {
    const groups = {};
    for (const item of filteredTypes) {
      const group = item.group || "other";
      if (!groups[group]) groups[group] = [];
      groups[group].push(item);
    }
    const ordered = GROUP_ORDER.filter((group) => groups[group]?.length).map(
      (group) => ({
        group,
        items: groups[group],
      })
    );
    const known = new Set(GROUP_ORDER);
    const extras = Object.keys(groups)
      .filter((group) => !known.has(group))
      .sort()
      .map((group) => ({
        group,
        items: groups[group],
      }));
    return [...ordered, ...extras];
  }, [filteredTypes]);

  const audienceTabLabel = (tab) =>
    translation[`email_test_tab_${tab}`] ||
    (tab === "direct" ? "Direct" : "Partner");

  const buildPayload = (type) => {
    const payload = {
      user_id: Number(userId),
      type,
      lang: lang || undefined,
    };
    if (toEmail.trim()) {
      payload.to_email = toEmail.trim();
    }
    if (type === "all") {
      payload.audience = audience;
    }
    return payload;
  };

  const validate = () => {
    if (!userId || !Number(userId)) {
      setErrorMsg(translation.email_test_user_id_required);
      return false;
    }
    return true;
  };

  const handleSend = async (type) => {
    if (!validate() || sendingType) return;
    setSendingType(type);
    setLastResult(null);
    try {
      const res = await Api.emailTest.send(buildPayload(type));
      setLastResult(res?.data || res);
      setSuccessMsg(
        type === "all"
          ? translation.email_test_all_sent
          : translation.email_test_sent
      );
    } catch (err) {
      setErrorMsg(err?.message || translation.something_went_wrong);
    } finally {
      setSendingType(null);
    }
  };

  const toggleGroup = (group) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  return (
    <WrapperSeamless>
      <Container>
        <Card className="shadow-xxl px-4 py-4 mb-4">
          <h4 className="font-weight-bold mb-1">{translation.email_test}</h4>
          <p className="text-black-50 mb-4">
            {translation.email_test_description}
          </p>
          <Box className="d-flex flex-wrap" style={{ gap: 12 }}>
            <TextField
              variant="outlined"
              className="my-2"
              label={translation.email_test_user_id}
              type="number"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              style={{ minWidth: 180 }}
              required
            />
            <TextField
              variant="outlined"
              className="my-2"
              label={translation.email_test_to_email}
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              style={{ minWidth: 260 }}
              helperText={translation.email_test_to_email_hint}
            />
            <Box style={{ minWidth: 180 }}>
              <Select
                label={translation.language || "Language"}
                value={lang}
                field="name"
                options={LANG_OPTIONS}
                onChange={(val) => setLang(val || "en")}
              />
            </Box>
          </Box>
          <Box className="d-flex align-items-center mt-2" style={{ gap: 12 }}>
            <Button
              variant="contained"
              color="primary"
              disabled={!!sendingType || loadingTypes}
              onClick={() => handleSend("all")}
            >
              {sendingType === "all" ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                translation.email_test_send_all
              )}
            </Button>
            <Button
              className="btn-neutral-primary"
              disabled={loadingTypes}
              onClick={loadTypes}
            >
              {translation.refresh || "Refresh"}
            </Button>
          </Box>
        </Card>

        <List
          component="div"
          className="my-3 nav-tabs justify-content-center nav-tabs-primary d-flex"
        >
          {AUDIENCE_TABS.map((tab) => (
            <ListItem
              key={tab}
              button
              disableRipple
              selected={audience === tab}
              onClick={() => setAudience(tab)}
            >
              {audienceTabLabel(tab)}
              <div className="divider" />
            </ListItem>
          ))}
        </List>

        {loadingTypes ? (
          <Box className="d-flex justify-content-center py-5">
            <CircularProgress />
          </Box>
        ) : (
          groupedTypes.map(({ group, items }) => {
            const isOpen = !collapsedGroups[group];
            return (
              <Card key={`${audience}-${group}`} className="shadow-xxl px-4 py-3 mb-4">
                <Box
                  className="d-flex align-items-center justify-content-between"
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => toggleGroup(group)}
                >
                  <h5 className="font-weight-bold mb-0 text-capitalize">
                    {translation[`email_test_group_${group}`] || group}
                  </h5>
                  <IconButton
                    size="small"
                    aria-expanded={isOpen}
                    aria-label={isOpen ? "Collapse" : "Expand"}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroup(group);
                    }}
                  >
                    <ExpandMoreIcon
                      style={{
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </IconButton>
                </Box>
                <Collapse in={isOpen}>
                  <Box mt={2}>
                    {items.map((item) => (
                      <Box
                        key={item.type}
                        className="d-flex align-items-center justify-content-between py-2"
                        style={{ borderBottom: "1px solid #eee" }}
                      >
                        <div>
                          <div className="font-weight-bold">{item.label}</div>
                          <div className="font-size-xs text-black-50">
                            {item.type}
                          </div>
                        </div>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          disabled={!!sendingType}
                          onClick={() => handleSend(item.type)}
                        >
                          {sendingType === item.type ? (
                            <CircularProgress size={18} />
                          ) : (
                            translation.send || "Send"
                          )}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Card>
            );
          })
        )}

        {lastResult && (
          <Card className="shadow-xxl px-4 py-3 mb-4">
            <h5 className="font-weight-bold mb-2">
              {translation.email_test_last_result}
            </h5>
            <pre
              className="mb-0"
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: 12,
              }}
            >
              {JSON.stringify(lastResult, null, 2)}
            </pre>
          </Card>
        )}
      </Container>
    </WrapperSeamless>
  );
}
